import { z } from "zod";
import { createCheckoutTitleSchema } from "@/lib/checkout-personal-info";
import { __ } from "@/lib/i18n/functions";
import { resolveCheckoutFulfillment } from "@/lib/checkout-delivery";
import { Locales } from "@/lib/i18n";
import { createPhoneSchema } from "@/lib/validation/phone";
import { BILLING_ADDRESS_REQUIREMENT_MESSAGE } from "@/lib/billing-address-eligibility";

export const createPaymentLocalisedSchema = (locale: Locales) => {
  const promoCodeSchema = z.string().min(3, __("Invalid Code", locale)).optional();

  const eventTypeSchema = z
    .string({ required_error: __("Type of event is required", locale) })
    .trim()
    .min(3, __("Invalid event", locale));

  const dateSchema = z.object({
    from: z.date({ required_error: __("Date is required", locale) }),
    to: z.date({ required_error: __("Date is required", locale) }),
  });

  const messageSchema = z.string().optional();

  const titleSchema = createCheckoutTitleSchema(__("Title is required", locale));

  const firstNameSchema = z
    .string({ required_error: __("First name is required", locale) })
    .trim()
    .min(3, __("Invalid First name", locale));

  const lastNameSchema = z
    .string({ required_error: __("Last name is required", locale) })
    .trim()
    .min(3, __("Invalid Last name", locale));

  const emailSchema = z
    .string({ required_error: __("Email is required", locale) })
    .trim()
    .email(__("Invalid email", locale));

  const phoneSchema = createPhoneSchema({
    required: __("Phone number is required", locale),
    invalid: __("Invalid phone number", locale),
  });

  const accountTypeSchema = z.enum(["individual", "company"], {
    required_error: __("Please select an account type", locale),
  });

  const wantsDeliverySchema = z.enum(["yes", "no"], {
    required_error: __("Please select an option", locale),
  });

  const wantsToRecoverItemsSchema = z.enum(["yes", "no"], {
    required_error: __("Please select an option", locale),
  });

  const deliveryAddressSchema = z.string({
    required_error: __("Please select a delivery address", locale),
  });

  const billingAddressSchema = z
    .string({ required_error: __(BILLING_ADDRESS_REQUIREMENT_MESSAGE, locale) })
    .trim()
    .min(1, __(BILLING_ADDRESS_REQUIREMENT_MESSAGE, locale));

  const checkoutSchema = z.object({
    promoCode: promoCodeSchema,
    eventType: eventTypeSchema,
    date: dateSchema,
    message: messageSchema,
    title: titleSchema,
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    email: emailSchema,
    phone: phoneSchema,
    accountType: accountTypeSchema,
    wantsDelivery: wantsDeliverySchema,
    deliveryDate: z.date({ required_error: __("Pickup date is required.", locale) }),
    wantsToRecoverItems: wantsToRecoverItemsSchema,
    recoveryDate: z.date({ required_error: __("Return date is required.", locale) }),
    deliveryAddress: deliveryAddressSchema,
    billingAddress: billingAddressSchema,
  });

  const promoCodeOnlySchema = checkoutSchema.pick({ promoCode: true });

  const eventInfoSchema = checkoutSchema.pick({
    eventType: true,
    date: true,
    message: true,
  });

  const personalInfoSchema = checkoutSchema.pick({
    title: true,
    firstName: true,
    lastName: true,
    email: true,
    phone: true,
  });

  const deliveryAndBillingSchema = checkoutSchema
    .pick({
      wantsDelivery: true,
      deliveryDate: true,
      wantsToRecoverItems: true,
      recoveryDate: true,
      accountType: true,
      deliveryAddress: true,
      billingAddress: true,
    })
    .superRefine((data, ctx) => {
      const fulfillment = resolveCheckoutFulfillment(data.wantsDelivery);

      if (fulfillment.requiresShippingAddress && !data.deliveryAddress) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: __("Please select a delivery address", locale),
          path: ["deliveryAddress"],
        });
      }
    });

  return {
    checkoutSchema,
    promoCodeOnlySchema,
    eventInfoSchema,
    personalInfoSchema,
    deliveryAndBillingSchema,
  };
};

type SchemaSet = ReturnType<typeof createPaymentLocalisedSchema>;

export type CheckoutSchema = z.infer<SchemaSet["checkoutSchema"]>;
export type PromoCodeSchema = z.infer<SchemaSet["promoCodeOnlySchema"]>;
export type EventInfoSchema = z.infer<SchemaSet["eventInfoSchema"]>;
export type PersonalInfoSchema = z.infer<SchemaSet["personalInfoSchema"]>;
export type DeliveryAndBillingSchema = z.infer<SchemaSet["deliveryAndBillingSchema"]>;
